import {alpha} from '@mui/material/styles';
import {gray, green, red, blue, warningOrange} from '../themePrimitives';

export const feedbackCustomizations = {
    MuiAlert: {
        styleOverrides: {
            root: ({theme}) => ({
                borderRadius: 10,
                color: (theme.vars || theme).palette.text.primary,
                border: '1px solid',
                '& .MuiAlert-message': {
                    flex: 1
                },
                '& .MuiAlert-action': {
                    padding: 0
                },
                '& .MuiButton-text': {
                    fontWeight: 'bold'
                },
                '& .MuiButton-colorInherit': {
                    boxShadow: 'none',
                    fontWeight: 'bold'
                },
                '& .MuiButton-root': {
                    minHeight: '2.25rem',
                    marginLeft: 'calc(1 * var(--template-spacing))'
                },
                variants: [
                    {
                        props: {
                            severity: 'normal'
                        },
                        style: {
                            backgroundColor: gray[50],
                            border: `1px solid ${alpha(gray[200], 0.5)}`,
                            '& .MuiAlert-icon': {
                                display: 'none'
                            },
                            '& .MuiButton-text': {
                                '&:hover': {
                                    backgroundColor: alpha(gray[300], 0.5)
                                }
                            },
                            '& .MuiButton-colorInherit': {
                                backgroundColor: alpha(gray[200], 0.5),
                                '&:hover': {
                                    backgroundColor: alpha(gray[300], 0.5)
                                }
                            }
                        }
                    }, {
                        props: {
                            severity: 'warning'
                        },
                        style: {
                            // Every variant gets the tinted surface, as success, error and
                            // info alerts do (see warningOrange).
                            backgroundColor: warningOrange.surface,
                            border: `1px solid ${warningOrange.border}`,
                            '& .MuiAlertTitle-root': {
                                color: '#000'
                            },
                            '& .MuiAlert-icon': {
                                color: warningOrange.icon
                            },
                            '& .MuiButton-colorInherit': {
                                backgroundColor: alpha(warningOrange.fill, 0.18),
                                color: alpha('#000', 0.85),
                                '&:hover': {
                                    backgroundColor: alpha(warningOrange.fill, 0.28)
                                }
                            }
                        }
                    },
                    {
                        props: {
                            severity: 'info'
                        },
                        style: {
                            backgroundColor: blue[100],
                            border: `1px solid ${alpha(blue[300], 0.5)}`,
                            '& .MuiAlertTitle-root': {
                                color: blue[500]
                            },
                            '& .MuiAlert-icon': {
                                color: blue[500]
                            },
                            '& .MuiButton-text': {
                                '&:hover': {
                                    backgroundColor: alpha(blue[200], 0.5)
                                }
                            },
                            '& .MuiButton-colorInherit': {
                                backgroundColor: alpha(blue[200], 0.5),
                                '&:hover': {
                                    backgroundColor: alpha(blue[300], 0.5)
                                }
                            }
                        }
                    },
                    {
                        props: {
                            severity: 'success'
                        },
                        style: {
                            backgroundColor: green[100],
                            border: `1px solid ${alpha(green[300], 0.5)}`,
                            '& .MuiAlertTitle-root': {
                                color: green[500]
                            },
                            '& .MuiAlert-icon': {
                                color: green[500]
                            },
                            '& .MuiButton-text': {
                                '&:hover': {
                                    backgroundColor: alpha(green[300], 0.5)
                                }
                            },
                            '& .MuiButton-colorInherit': {
                                backgroundColor: alpha(green[200], 0.5),
                                '&:hover': {
                                    backgroundColor: alpha(green[300], 0.5)
                                }
                            }
                        }
                    },
                    {
                        props: {
                            severity: 'error'
                        },
                        style: {
                            backgroundColor: red[50],
                            border: `1px solid ${alpha(red[100], 0.5)}`,
                            '& .MuiAlert-icon': {
                                color: red[500]
                            },
                            '& .MuiAlertTitle-root': {
                                color: red[500]
                            },
                            '& .MuiButton-text': {
                                '&:hover': {
                                    backgroundColor: alpha(red[100], 0.5)
                                }
                            },
                            '& .MuiButton-colorInherit': {
                                color: '#000',
                                backgroundColor: alpha(red[200], 0.5),
                                '&:hover': {
                                    backgroundColor: alpha(red[300], 0.5)
                                }
                            }
                        }
                    },
                    {
                        props: {
                            size: 'small'
                        },
                        style: {
                            padding: 8,
                            fontSize: '0.8125rem',
                            borderRadius: 8,
                            alignItems: 'center',
                            '& .MuiAlert-icon': {
                                padding: 0,
                                marginRight: 8,
                                fontSize: '1rem'
                            },
                            '& .MuiAlert-message': {
                                padding: 0,
                                fontSize: '0.8125rem'
                            },
                            '& .MuiAlert-action': {
                                padding: 0,
                                marginRight: 0,
                                alignItems: 'center'
                            },
                            '& .MuiButton-root': {
                                height: 28,
                                minHeight: 28,
                                padding: '0 10px',
                                fontSize: '0.75rem',
                                lineHeight: 1,
                                fontWeight: 600,
                                marginLeft: 4
                            }
                        }
                    }
                ]
            })
        }
    },
    MuiDialog: {
        styleOverrides: {
            root: () => ({
                // No 1px border on the paper. It sits on a dark backdrop, so contrast
                // already defines its edge — and the border collided with any content
                // running flush to that edge (the JPR checklist's coloured status
                // bars), leaving a broken hairline down the side where the two met.
                // Overflow is left alone deliberately: MUI manages it per `scroll`
                // mode, and clipping it here would be a global change with a far
                // wider blast radius than the problem.
                '& .MuiDialog-paper': {
                    borderRadius: '10px'
                },
                '& .MuiDialogTitle-root+.MuiDialogContent-root': {
                    paddingTop: 20
                }
            })
        }
    },
    MuiDialogContent: {
        styleOverrides: {
            root: ({theme}) => ({
                backgroundColor: (theme.vars || theme).palette.background.paper
            })
        }
    },
    MuiLinearProgress: {
        styleOverrides: {
            root: () => ({
                margin: '5px 0',
                height: 8,
                borderRadius: 8,
                backgroundColor: gray[200]
            })
        }
    }
};
